/**
 * Centralized HTTP Client
 * 
 * A configured axios instance with interceptors for:
 * - Request/response logging
 * - Error handling
 * - Authentication tokens
 * - Request timing
 */

import axios from 'axios';
import logger from '../logger';

// Get API base URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3003';

// Create axios instance
const httpClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor
 * - Add authentication token if available
 * - Log request details
 */
httpClient.interceptors.request.use(
  (config) => {
    const startTime = Date.now();
    
    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Store start time for duration calculation
    config.metadata = { startTime };

    // Log request
    logger.logRequest(
      config.method.toUpperCase(),
      config.url,
      config.data,
      config.headers
    );

    return config;
  },
  (error) => {
    logger.error('HTTP Client Request Error', error, {
      url: error.config?.url,
      method: error.config?.method,
    });
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * - Log response details
 * - Handle common errors
 * - Calculate request duration
 */
httpClient.interceptors.response.use(
  (response) => {
    const duration = response.config.metadata?.startTime
      ? Date.now() - response.config.metadata.startTime
      : null;

    logger.logResponse(
      response.config.method.toUpperCase(),
      response.config.url,
      response.status,
      response.data,
      duration
    );

    return response;
  },
  (error) => {
    const duration = error.config?.metadata?.startTime
      ? Date.now() - error.config.metadata.startTime
      : null;

    // Extract error details
    const errorDetails = {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
      duration: duration ? `${duration}ms` : null,
    };

    // Log error
    logger.error('HTTP Client Response Error', error, errorDetails);

    // Handle specific error cases
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login if needed
          localStorage.removeItem('auth_token');
          logger.warn('Authentication token expired or invalid');
          break;
        case 403:
          logger.warn('Access forbidden', { url: error.config?.url });
          break;
        case 404:
          logger.warn('Resource not found', { url: error.config?.url });
          break;
        case 500:
          logger.error('Server error', null, { url: error.config?.url });
          break;
        default:
          logger.warn(`HTTP Error ${status}`, errorDetails);
      }

      // Return a normalized error format
      return Promise.reject({
        ...error,
        message: data?.error || data?.message || error.message,
        status,
        data: data,
        isAxiosError: true,
      });
    } else if (error.request) {
      // Request made but no response received
      logger.error('Network error - no response received', error, {
        url: error.config?.url,
      });
      
      return Promise.reject({
        ...error,
        message: 'Network error. Please check your internet connection.',
        isNetworkError: true,
      });
    } else {
      // Something else happened
      logger.error('Request setup error', error);
      return Promise.reject(error);
    }
  }
);

/**
 * Helper methods for different HTTP methods
 */
export const get = async (url, config = {}) => {
  const response = await httpClient.get(url, config);
  return response.data;
};

export const post = async (url, data = {}, config = {}) => {
  const response = await httpClient.post(url, data, config);
  return response.data;
};

export const put = async (url, data = {}, config = {}) => {
  const response = await httpClient.put(url, data, config);
  return response.data;
};

export const patch = async (url, data = {}, config = {}) => {
  const response = await httpClient.patch(url, data, config);
  return response.data;
};

export const del = async (url, config = {}) => {
  const response = await httpClient.delete(url, config);
  return response.data;
};

// Export the configured axios instance as well
export default httpClient;

